import { useBackendApi } from './backendApi';
import { ProjectType, TaskType } from './Types';
import { useCacheService } from './cacheService';
import { useSetupAreaIdContext } from './SetupAreaIdContext';

enum TagType {
  TAG = 'tag',
  MENTION = 'mention',
}

type Tag = {
  id: string;
  value: string;
  type?: TagType;
  isDeprecated: boolean;
};

export function replaceTagsIdsWithValues(
  input: string,
  availableTags: Tag[]
): string {
  let result = input;

  if (result.includes('#') || result.includes('@')) {
    const tags = result.match(/[#|@]([\w\\.]+)/g) || [];

    const uniqueTags: string[] = Array.from(new Set(tags));

    uniqueTags.forEach((tag) => {
      const startSymbol = tag[0];
      const tagName = tag.substring(1);
      const tagObject = availableTags.find((t) => t.id === tagName);

      if (tagObject) {
        result = result.replace(
          new RegExp(tag.replace(/\./g, '\\.'), 'g'),
          `${startSymbol}${tagObject.value}`
        );
      }
    });
  }

  return result;
}

export function useSmartTagsService() {
  const nekstApi = useBackendApi();

  const cacheService = useCacheService('smart-tags-cache');

  const setupAreaIdContext = useSetupAreaIdContext();

  const filterTagsByType = (tags: Tag[], tagType?: TagType | 'all') => {
    if (tagType === 'all') {
      return tags;
    } else {
      const desiredTagType = tagType || TagType.TAG;
      return tags.filter((tag) => tag.type === desiredTagType);
    }
  };

  const getSmartTags = async (
    projectType?: ProjectType,
    taskType?: TaskType,
    projectId?: number,
    tagType?: TagType | 'all',
    setupAreaId: number | string | undefined = undefined
  ) => {
    const { setupAreaId: contextSetupAreaId } = setupAreaIdContext || {};

    setupAreaId = setupAreaId || contextSetupAreaId;

    const allTags = await cacheService.get<Tag[]>(
      `smart-tags-${projectType}-${taskType}-${projectId}-${setupAreaId}-${
        tagType || ''
      }`,
      async (cacheItem) => {
        cacheItem.setExpireInSeconds(60);

        return (await nekstApi.get(
          `/data/smarttags?taskType=${taskType || ''}&projectType=${
            projectType || ''
          }&projectId=${projectId || ''}&setupAreaId=${setupAreaId || ''}`
        )) as Tag[];
      }
    );

    return filterTagsByType(allTags, tagType);
  };

  const getSmartTagsByProjectId = async (
    projectId: number,
    taskType?: TaskType,
    tagType?: TagType | 'all'
  ) => {
    const result = await cacheService.get(
      `smart-tags-project-${projectId}-${taskType || ''}-${tagType || ''}`,
      async (cacheItem) => {
        cacheItem.setExpireInSeconds(60);
        return (await nekstApi.get(
          `/projects/${projectId}/smarttagsoptions?taskType=${taskType || ''}`
        )) as Tag[];
      }
    );

    return filterTagsByType(result, tagType);
  };

  const getSmartTagsByPlanId = async (
    planId: number,
    taskType?: TaskType,
    tagType?: TagType | 'all'
  ) => {
    const result = await cacheService.get(
      `smart-tags-plan-${planId}-${taskType || ''}`,
      async (cacheItem) => {
        cacheItem.setExpireInSeconds(60);
        return (await nekstApi.get(
          `/plans/${planId}/smarttagsoptions?taskType=${taskType || ''}`
        )) as Tag[];
      }
    );

    return filterTagsByType(result, tagType);
  };

  const previewSmartTags = async (
    projectId: number,
    text: string
  ): Promise<{ text: string }> => {
    return await nekstApi.post(`/projects/${projectId}/smarttagspreview`, {
      text,
    });
  };

  return {
    getSmartTags,
    getSmartTagsByProjectId,
    getSmartTagsByPlanId,
    previewSmartTags,
  };
}
